import Button from '@mui/material/Button';
import { useTheme } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import type { ButtonProps } from '@mui/material/Button';

type SharedButtonVariant = 'primary' | 'secondary';

export interface SharedButtonProps extends Omit<ButtonProps, 'variant'> {
  variant?: SharedButtonVariant;
  color?: ButtonProps['color'];
  loading?: boolean;
}

export default function SharedButton({
  variant = 'primary',
  color,
  loading = false,
  disabled = false,
  children,
  sx,
  ...props
}: SharedButtonProps) {
  const theme = useTheme();

  const isPrimary = variant === 'primary';

  let buttonSx = {};

  if (color === 'error') {
    buttonSx = {
      color: isPrimary
        ? theme.palette.semantic.button.primary.text
        : theme.palette.semantic.button.error.border,
      border: '1px solid',
      borderColor: theme.palette.semantic.button.error.border,
      backgroundColor: isPrimary
        ? theme.palette.semantic.button.error.background
        : 'unset',
      '&:hover': {
        backgroundColor: isPrimary
          ? theme.palette.semantic.button.error.hover
          : theme.palette.semantic.card.error,
        borderColor: theme.palette.semantic.button.error.hover,
      },
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
        borderColor: theme.palette.action.disabledBackground,
        backgroundColor: isPrimary
          ? theme.palette.action.disabledBackground
          : 'unset',
      },
    };
  } else if (color === 'warning') {
    buttonSx = {
      color: isPrimary
        ? theme.palette.semantic.button.primary.text
        : theme.palette.semantic.button.warning.border,
      border: '1px solid',
      borderColor: theme.palette.semantic.button.warning.border,
      backgroundColor: isPrimary
        ? theme.palette.semantic.button.warning.background
        : 'unset',
      '&:hover': {
        backgroundColor: isPrimary
          ? theme.palette.semantic.button.warning.hover
          : theme.palette.semantic.card.warning,
        borderColor: theme.palette.semantic.button.warning.hover,
      },
      '&.Mui-disabled': {
        color: theme.palette.action.disabled,
        borderColor: theme.palette.action.disabledBackground,
        backgroundColor: isPrimary
          ? theme.palette.action.disabledBackground
          : 'unset',
      },
    };
  } else {
    buttonSx = isPrimary
      ? {
          backgroundColor: theme.palette.semantic.button.primary.background,
          border: '1px solid',
          borderColor: theme.palette.semantic.button.primary.background,
          color: theme.palette.semantic.button.primary.text,
          '&:hover': {
            borderColor: theme.palette.semantic.button.primary.hover,
            backgroundColor: theme.palette.semantic.button.primary.hover,
          },
          '&.Mui-disabled': {
            backgroundColor: theme.palette.action.disabledBackground,
            color: theme.palette.action.disabled,
            borderColor: theme.palette.action.disabledBackground,
          },
        }
      : {
          color: theme.palette.semantic.button.secondary.text,
          borderColor: theme.palette.semantic.button.secondary.border,
          '&:hover': {
            backgroundColor: theme.palette.semantic.button.secondary.hover,
          },
          '&.Mui-disabled': {
            color: theme.palette.action.disabled,
            borderColor: theme.palette.action.disabledBackground,
          },
        };
  }

  return (
    <Button
      {...props}
      variant={isPrimary ? 'contained' : 'outlined'}
      color={color}
      disabled={disabled || loading}
      sx={[
        {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: '4px',
          px: 1.5,
          py: 0.75,
          position: 'relative',
          ...buttonSx,
        },
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
      ]}
    >
      {loading && (
        <CircularProgress
          size={16}
          color="inherit"
          sx={{
            position: 'absolute',
            left: '50%',
            top: '50%',
            marginTop: '-8px',
            marginLeft: '-8px',
          }}
        />
      )}
      <span
        style={{
          visibility: loading ? 'hidden' : 'visible',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
        }}
      >
        {children}
      </span>
    </Button>
  );
}
